<template>
  <b-modal
    ref="dfCorrectionHistoryModal"
    size="xl"
    centered
    hide-header
    hide-footer
    body-class="p-0"
    modal-class="df-hx-modal"
    @hidden="resetData"
  >
    <div class="df-hx">
      <header class="df-hx__header">
        <div class="df-hx__title">
          <svg
            class="df-hx__title-icon"
            width="16"
            height="16"
            viewBox="0 0 20 20"
            aria-hidden="true"
          >
            <path
              d="M3 14 L12 5 L15 8 L6 17 L3 17 Z M13 4 L15 2 L18 5 L16 7 Z"
              stroke="currentColor"
              stroke-width="1.4"
              fill="none"
              stroke-linejoin="round"
            />
          </svg>
          <span>ประวัติการแก้ไข DF</span>
        </div>
        <button
          type="button"
          class="df-hx__close"
          aria-label="ปิด"
          @click="hide"
        >
          ✕
        </button>
      </header>

      <section class="df-hx__context">
        <div>
          <span class="df-hx__context-label">คนไข้ </span>
          <b>{{ currentItem.patientFullName || "-" }}</b>
        </div>
        <div>
          <span class="df-hx__context-label">DN </span>
          <b>{{ currentItem.dn || "-" }}</b>
        </div>
        <div>
          <span class="df-hx__context-label">รายการ </span>
          <b>{{ currentItem.itemName || "-" }}</b>
        </div>
        <div class="df-hx__context-meta">
          {{ contextMeta }}
        </div>
      </section>

      <div v-if="isLoading" class="df-hx__placeholder">
        <b-spinner small class="mr-2" />กำลังโหลด...
      </div>

      <template v-else-if="historyList.length === 0">
        <div class="df-hx__empty">
          <div class="df-hx__empty-icon">
            <svg width="26" height="26" viewBox="0 0 24 24" aria-hidden="true">
              <path
                d="M12 8 V13 M12 16 V16.5 M3 12 A9 9 0 1 1 12 21"
                stroke="currentColor"
                stroke-width="1.5"
                fill="none"
                stroke-linecap="round"
              />
            </svg>
          </div>
          <div class="df-hx__empty-title">ยังไม่มีประวัติการแก้ไข</div>
          <div class="df-hx__empty-body">
            รายการนี้ยังไม่เคยถูกแก้ไข ค่า DF และ CF ตรงกับใบเสร็จต้นฉบับ
          </div>
        </div>
      </template>

      <section v-else class="df-hx__body">
        <div class="df-hx__legend">
          <span>เน้นเฉพาะช่องที่เปลี่ยน · ใหม่ → เก่า</span>
          <span class="df-hx__pill df-hx__pill--neutral df-hx__legend-count">
            {{ historyList.length }} ครั้ง
          </span>
        </div>

        <div :class="['df-hx__table-wrap', { scrollable: isScrollable }]">
          <table class="df-hx__table">
            <thead>
              <tr>
                <th class="col-when">เมื่อ</th>
                <th class="col-who">ผู้แก้ไข</th>
                <th class="col-doctor">แพทย์</th>
                <th class="col-num">DF</th>
                <th class="col-num">CF</th>
              </tr>
            </thead>
            <tbody>
              <template v-for="(row, i) in decoratedRows">
                <tr
                  :key="`r-${i}`"
                  :class="[
                    'df-hx__row',
                    { 'is-quiet': row.quiet, 'has-divider': i > 0 },
                  ]"
                >
                  <td class="col-when">
                    <div class="df-hx__when-time">{{ row.when }}</div>
                    <div class="df-hx__when-date">{{ row.date }}</div>
                  </td>
                  <td class="col-who">
                    <div class="df-hx__who">
                      <span class="df-hx__avatar">{{ row.avatar }}</span>
                      <div>
                        <div class="df-hx__who-name">{{ row.who }}</div>
                        <div class="df-hx__who-pills">
                          <span
                            v-if="row.latest"
                            class="df-hx__pill df-hx__pill--primary"
                          >
                            ล่าสุด
                          </span>
                          <span
                            v-if="row.initial"
                            class="df-hx__pill df-hx__pill--neutral"
                          >
                            บันทึกครั้งแรก
                          </span>
                          <span
                            v-if="row.quiet"
                            class="df-hx__pill df-hx__pill--neutral"
                          >
                            ไม่มีค่าตัวเลขเปลี่ยน
                          </span>
                          <span
                            v-if="row.cancelled"
                            class="df-hx__pill df-hx__pill--neutral"
                          >
                            ยกเลิก
                          </span>
                        </div>
                      </div>
                    </div>
                  </td>
                  <td class="col-doctor">
                    <span v-if="!row.doctor" class="df-hx__dot">·</span>
                    <span v-else class="df-hx__diff">
                      <span class="df-hx__diff-before">{{
                        row.doctor.before
                      }}</span>
                      <svg
                        class="df-hx__arrow"
                        width="12"
                        height="10"
                        viewBox="0 0 12 10"
                        aria-hidden="true"
                      >
                        <path
                          d="M1 5 H10 M7 2 L10 5 L7 8"
                          stroke="currentColor"
                          stroke-width="1.2"
                          fill="none"
                          stroke-linecap="round"
                          stroke-linejoin="round"
                        />
                      </svg>
                      <b class="df-hx__diff-after">{{ row.doctor.after }}</b>
                    </span>
                  </td>
                  <td class="col-num">
                    <span v-if="!row.df" class="df-hx__dot">·</span>
                    <span v-else class="df-hx__diff df-hx__diff--mono">
                      <span class="df-hx__diff-before">{{
                        formatNumber(row.df.before)
                      }}</span>
                      <svg
                        class="df-hx__arrow"
                        width="12"
                        height="10"
                        viewBox="0 0 12 10"
                        aria-hidden="true"
                      >
                        <path
                          d="M1 5 H10 M7 2 L10 5 L7 8"
                          stroke="currentColor"
                          stroke-width="1.2"
                          fill="none"
                          stroke-linecap="round"
                          stroke-linejoin="round"
                        />
                      </svg>
                      <b class="df-hx__diff-after">{{
                        formatNumber(row.df.after)
                      }}</b>
                    </span>
                  </td>
                  <td class="col-num">
                    <span v-if="!row.cf" class="df-hx__dot">·</span>
                    <span v-else class="df-hx__diff df-hx__diff--mono">
                      <span class="df-hx__diff-before">{{
                        formatNumber(row.cf.before)
                      }}</span>
                      <svg
                        class="df-hx__arrow"
                        width="12"
                        height="10"
                        viewBox="0 0 12 10"
                        aria-hidden="true"
                      >
                        <path
                          d="M1 5 H10 M7 2 L10 5 L7 8"
                          stroke="currentColor"
                          stroke-width="1.2"
                          fill="none"
                          stroke-linecap="round"
                          stroke-linejoin="round"
                        />
                      </svg>
                      <b
                        :class="[
                          'df-hx__diff-after',
                          { 'is-warn': row.cfNegative },
                        ]"
                      >
                        {{ formatNumber(row.cf.after) }}
                      </b>
                    </span>
                  </td>
                </tr>
                <tr
                  :key="`n-${i}`"
                  :class="['df-hx__note-row', { 'is-quiet': row.quiet }]"
                >
                  <td colspan="5">
                    <div v-if="row.note" class="df-hx__note">
                      <span class="df-hx__note-label">หมายเหตุ</span>
                      <span class="df-hx__note-text">“{{ row.note }}”</span>
                    </div>
                    <div v-else class="df-hx__note-empty">ไม่มีหมายเหตุ</div>
                    <div v-if="row.cfNegative" class="df-hx__note-warn">
                      <span class="df-hx__pill df-hx__pill--warn">
                        ⚠ CF ติดลบ — คลินิกรับภาระส่วนต่าง
                      </span>
                    </div>
                  </td>
                </tr>
              </template>
            </tbody>
          </table>
        </div>
      </section>

      <footer class="df-hx__footer">
        <div class="df-hx__footer-meta">{{ footerMeta }}</div>
        <button type="button" class="df-hx__btn" @click="hide">ปิด</button>
      </footer>
    </div>
  </b-modal>
</template>

<script>
import moment from "moment";

const SCROLL_THRESHOLD = 4;

export default {
  name: "DfCorrectionHistoryModal",
  data() {
    return {
      currentItem: {},
      historyList: [],
      isLoading: false,
    };
  },
  computed: {
    contextMeta() {
      const dt = this.currentItem.creationDt;
      const formatted = dt
        ? moment(dt)
            .locale("th")
            .format("D MMM YY")
        : "";
      const no = this.currentItem.receiptNo;
      if (formatted && no) return `${formatted} · ใบเสร็จ #${no}`;
      if (no) return `ใบเสร็จ #${no}`;
      return formatted || "";
    },
    isScrollable() {
      return this.historyList.length > SCROLL_THRESHOLD;
    },
    decoratedRows() {
      const fallbackDoctor = this.currentItem.doctorFullName || "-";
      const lastIndex = this.historyList.length - 1;
      return this.historyList.map((item, idx) => {
        const doctor =
          item.previousDoctorName != null
            ? {
                before: item.previousDoctorName || "—",
                after:
                  item.correctedDoctorName || fallbackDoctor || "—",
              }
            : null;
        // Display paid_df / paid_cf — same source the DF reports read via
        // IFNULL(dc.corrected_paid_df, reci.paid_df). Matches the doctor's
        // actual cash share users see in reports.
        const df =
          item.previousPaidDf != null
            ? { before: item.previousPaidDf, after: item.correctedPaidDf }
            : null;
        const cf =
          item.previousPaidCf != null
            ? { before: item.previousPaidCf, after: item.correctedPaidCf }
            : null;
        const quiet = !doctor && !df && !cf;
        const cfNegative =
          cf && typeof cf.after === "number" && cf.after < 0;
        return {
          when: this.formatTime(item.createdAt),
          date: this.formatDate(item.createdAt),
          who: item.correctedByName || "-",
          avatar: this.makeAvatar(item.correctedByName),
          doctor,
          df,
          cf,
          cfNegative,
          note: item.remark || "",
          latest: idx === 0,
          initial: idx === lastIndex && lastIndex > 0,
          quiet,
          cancelled: !!item.cancelled,
        };
      });
    },
    footerMeta() {
      const n = this.historyList.length;
      if (!n) return "";
      const base = `แสดง ${n} จาก ${n} รายการ`;
      return this.isScrollable ? `${base} · เลื่อนเพื่อดูทั้งหมด` : base;
    },
  },
  methods: {
    async show(item) {
      this.currentItem = { ...item };
      this.$refs.dfCorrectionHistoryModal.show();
      this.isLoading = true;
      try {
        const res = await this.$store.dispatch(
          "moduleDfCorrection/fetchDfCorrectionHistory",
          {
            clinicUrl: this.$route.params.clinicUrl,
            branchUrl: this.$route.params.branchUrl,
            receiptId: item.receiptId || item.id,
            receiptItemId: item.receiptItemId,
          }
        );
        this.historyList = res.data || [];
      } catch (err) {
        this.historyList = [];
      } finally {
        this.isLoading = false;
      }
    },
    hide() {
      this.$refs.dfCorrectionHistoryModal.hide();
    },
    resetData() {
      this.currentItem = {};
      this.historyList = [];
    },
    formatTime(dt) {
      return dt ? moment(dt).format("HH:mm") : "";
    },
    formatDate(dt) {
      return dt
        ? moment(dt)
            .locale("th")
            .format("D MMM YY")
        : "";
    },
    formatNumber(num) {
      if (num == null) return "-";
      if (typeof num !== "number") return num;
      return (Math.round(num * 100) / 100).toLocaleString("en-US");
    },
    makeAvatar(name) {
      if (!name) return "?";
      const trimmed = String(name).trim();
      return trimmed ? trimmed.charAt(0) : "?";
    },
  },
};
</script>

<style scoped>
.df-hx {
  font-family: "Sarabun", "IBM Plex Sans Thai", system-ui, -apple-system,
    sans-serif;
  color: #1f2933;
  background: #ffffff;
}

.df-hx__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 20px;
  border-bottom: 1px solid #e5e7eb;
}
.df-hx__title {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 17px;
  font-weight: 600;
  color: #1f2933;
}
.df-hx__title-icon {
  color: #6b7280;
}
.df-hx__close {
  background: transparent;
  border: 0;
  color: #9ca3af;
  font-size: 18px;
  line-height: 1;
  cursor: pointer;
  padding: 4px 8px;
}
.df-hx__close:hover {
  color: #6b7280;
}

.df-hx__context {
  padding: 12px 20px;
  background: #f3f4f6;
  border-bottom: 1px solid #e5e7eb;
  font-size: 13px;
  color: #1f2933;
  display: flex;
  gap: 22px;
  flex-wrap: wrap;
  align-items: baseline;
}
.df-hx__context-label {
  color: #6b7280;
}
.df-hx__context-meta {
  margin-left: auto;
  color: #6b7280;
}

.df-hx__placeholder {
  padding: 40px 20px;
  text-align: center;
  color: #6b7280;
  font-size: 14px;
}

.df-hx__empty {
  padding: 60px 20px;
  text-align: center;
  color: #6b7280;
}
.df-hx__empty-icon {
  width: 56px;
  height: 56px;
  margin: 0 auto 14px;
  border-radius: 50%;
  background: #f3f4f6;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #9ca3af;
}
.df-hx__empty-title {
  font-size: 15px;
  color: #1f2933;
  font-weight: 600;
  margin-bottom: 4px;
}
.df-hx__empty-body {
  font-size: 13px;
}

.df-hx__body {
  padding: 10px 20px 6px;
}
.df-hx__legend {
  font-size: 12px;
  color: #6b7280;
  display: flex;
  gap: 10px;
  align-items: center;
  margin-bottom: 6px;
}
.df-hx__legend-count {
  margin-left: auto;
}

.df-hx__pill {
  display: inline-block;
  border-radius: 999px;
  font-size: 11px;
  padding: 1px 8px;
  font-weight: 500;
  line-height: 1.5;
  white-space: nowrap;
  border: 1px solid transparent;
}
.df-hx__pill--neutral {
  background: #f3f4f6;
  color: #6b7280;
  border-color: #e5e7eb;
}
.df-hx__pill--primary {
  background: #eef4ff;
  color: #2a6fdb;
  border-color: #eef4ff;
}
.df-hx__pill--warn {
  background: #fdf0e8;
  color: #c8501f;
  border-color: #fdf0e8;
}

.df-hx__table-wrap.scrollable {
  max-height: 360px;
  overflow-y: auto;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
}
.df-hx__table-wrap.scrollable thead th {
  position: sticky;
  top: 0;
  background: #ffffff;
  z-index: 1;
}

.df-hx__table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
}
.df-hx__table thead th {
  text-align: left;
  padding: 8px;
  font-weight: 600;
  color: #6b7280;
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  border-bottom: 1px solid #e5e7eb;
}
.df-hx__table thead th.col-num {
  text-align: right;
}
.df-hx__table thead th.col-when {
  width: 120px;
}
.df-hx__table thead th.col-who {
  width: 220px;
}
.df-hx__table thead th.col-num {
  width: 150px;
}

.df-hx__table td {
  padding: 12px 8px 2px;
  vertical-align: top;
}
.df-hx__table td.col-num {
  text-align: right;
}
.df-hx__row.has-divider > td {
  border-top: 1px solid #e5e7eb;
}
.df-hx__row.is-quiet,
.df-hx__note-row.is-quiet {
  opacity: 0.55;
}

.df-hx__when-time {
  font-weight: 600;
  color: #1f2933;
}
.df-hx__when-date {
  font-size: 11px;
  color: #6b7280;
}

.df-hx__who {
  display: flex;
  align-items: center;
  gap: 8px;
}
.df-hx__avatar {
  display: inline-flex;
  width: 26px;
  height: 26px;
  border-radius: 50%;
  background: #eef4ff;
  color: #2a6fdb;
  font-size: 12px;
  font-weight: 600;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.df-hx__who-name {
  color: #1f2933;
}
.df-hx__who-pills {
  margin-top: 2px;
  display: flex;
  gap: 4px;
  flex-wrap: wrap;
}

.df-hx__dot {
  color: #9ca3af;
  font-family: "JetBrains Mono", "Cousine", monospace;
  font-size: 13px;
}

.df-hx__diff {
  display: inline-flex;
  gap: 6px;
  align-items: baseline;
  font-size: 13px;
  white-space: nowrap;
}
.df-hx__diff--mono {
  font-family: "JetBrains Mono", "Cousine", monospace;
  font-variant-numeric: tabular-nums;
}
.col-num .df-hx__diff {
  justify-content: flex-end;
}
.df-hx__diff-before {
  color: #9ca3af;
  text-decoration: line-through;
  text-decoration-thickness: 1px;
}
.df-hx__arrow {
  color: #9ca3af;
  flex-shrink: 0;
  align-self: center;
}
.df-hx__diff-after {
  color: #1f2933;
  font-weight: 600;
}
.df-hx__diff-after.is-warn {
  color: #c8501f;
}

.df-hx__note-row td {
  padding: 2px 8px 14px;
}
.df-hx__note {
  display: flex;
  gap: 8px;
  align-items: baseline;
  font-size: 12px;
  color: #6b7280;
}
.df-hx__note-label {
  font-size: 10px;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  flex-shrink: 0;
  padding-top: 2px;
}
.df-hx__note-text {
  color: #1f2933;
  font-size: 13px;
  line-height: 1.55;
}
.df-hx__note-empty {
  font-size: 12px;
  color: #9ca3af;
  font-style: italic;
}
.df-hx__note-warn {
  margin-top: 6px;
  padding-left: 70px;
}

.df-hx__footer {
  border-top: 1px solid #e5e7eb;
  padding: 12px 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: #ffffff;
}
.df-hx__footer-meta {
  font-size: 12px;
  color: #9ca3af;
}
.df-hx__btn {
  background: #2a6fdb;
  color: #ffffff;
  border: 0;
  padding: 7px 22px;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
}
.df-hx__btn:hover {
  background: #2360c2;
}
</style>

<style>
.df-hx-modal .modal-content {
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  box-shadow: 0 8px 28px rgba(15, 23, 42, 0.12);
  overflow: hidden;
}
</style>
